import {Component, DestroyRef, EventEmitter, inject, OnInit, Output} from '@angular/core';
import {ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';

import {Observable, shareReplay} from 'rxjs';
import {Domain} from '../../../models/client/domain';
import {
  enumToIbpSelectOptions,
  IbpSelectComponent,
  IbpSelectOption
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-select/ibp-select.component';
import {map, tap, withLatestFrom} from 'rxjs/operators';
import {DomainService} from '../../../service/domain.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateModule, TranslatePipe, TranslateService} from '@ngx-translate/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import {CompanyLegalType} from '../../../enums/companyLegalType';

import {ClientCreationService} from "../services/client-creation.service";
import {IbpTooltipDirective} from "../../../ibp-directives/ibp-tooltip/ibp-tooltip.directive";
import {TitleLabel} from "../../../enums/title-label.enum";
import {StateService} from "../../../service/state/state.service";


import {IbpButtonComponent} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-button/ibp-button.component';
import {
  IbpTextInputComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-text-input/ibp-text-input.component';
import {IbpFormHeaderComponent} from "../../../ibp-components/ibp-form-header/ibp-form-header.component";
import {
  CompanyDomainInfoComponent
} from "../../../client-settings/company-properties/company-domain-info/company-domain-info.component";

@Component({
  selector: 'ibp-company-info-step',
  templateUrl: './company-info-step.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    AsyncPipe,
    IbpTooltipDirective,
    CommonModule,
    TranslateModule,
    IbpButtonComponent,
    IbpSelectComponent,
    IbpTextInputComponent,
    IbpFormHeaderComponent,
    CompanyDomainInfoComponent
],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    }],
})
export class CompanyInfoStepComponent implements OnInit {
  private rootFormGroup: FormGroupDirective = inject(FormGroupDirective);
  private domainService: DomainService = inject(DomainService);
  private translate: TranslateService = inject(TranslateService);
  private clientCreationService: ClientCreationService = inject(ClientCreationService);
  private stateService: StateService = inject(StateService);

  readonly #destroyRef = inject(DestroyRef);

  @Output() public nextCallback = new EventEmitter();
  @Output() public cancelCallback = new EventEmitter();

  public form: FormGroup;
  public domain$: Observable<Domain>;
  public domains$: Observable<Domain[]>;
  public domainOptions$: Observable<IbpSelectOption[]>;
  public corporateFormOptions: IbpSelectOption[];
  public countryOptions: IbpSelectOption[];
  public titleOptions: IbpSelectOption[];

  public ngOnInit() {
    this.buildForm();
    this.loadOptions();
    this.observeDomainChange();
  }

  public isFormDisabledOrValid() {
    return this.form.get('companyInfo').disabled || this.form.get('companyInfo').valid;
  }

  public onNextClick() {
    this.clientCreationService.setCompanyInfoData(this.form);
    this.nextCallback.emit()
  }

  private buildForm(): void {
    this.form = this.rootFormGroup.control;
  }

  private loadOptions(): void {
    this.prepareTitleOptions();
    this.prepareCountryOptions();

    this.domains$ = this.domainService.findAllForUser().pipe(shareReplay(1), takeUntilDestroyed(this.#destroyRef));
    this.domainOptions$ = this.domains$.pipe(map(domains => domains.map(domain => ({label: domain.displayName, value: domain.id}))));
    this.corporateFormOptions = enumToIbpSelectOptions(this.translate, CompanyLegalType, "COMPANY_SETTINGS.FORM.COMPANY_LEGAL_TYPE.");
  }

  private prepareTitleOptions() {
    this.stateService.user$.pipe(
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe(user => this.prepareLocaleDependentTitleOptions(user.locale));
  }

  private prepareLocaleDependentTitleOptions(locale: string) {
    let titleOptions = enumToIbpSelectOptions(this.translate, TitleLabel, 'GENERAL.TITLE.');
    if (locale.includes("DE")) {
      titleOptions = titleOptions.filter(to => ["MR", "MS"].includes(to.value))
    } else if (locale.includes("FR")) {
      titleOptions = titleOptions.filter(to => ["MR", "MRS", "MISS"].includes(to.value))
    }

    this.titleOptions = titleOptions;
  }

  private prepareCountryOptions() {
    this.countryOptions = [
      {
        label: this.translate.instant('COMPANY_PROPERTIES.FORM.COUNTRY.CH'),
        value: 'CH',
      },
      {
        label: this.translate.instant('COMPANY_PROPERTIES.FORM.COUNTRY.DE'),
        value: 'DE',
      }]
  }

  private observeDomainChange() {
    this.domain$ = this.form.get('companyInfo.domain').valueChanges
      .pipe(
        withLatestFrom(this.domains$),
        map(([domainId, domains]: [number, Domain[]]) => domains.find((domain: Domain) => domain.id == domainId)),
        tap(() => this.resetDomainRelatedServicesSettings()),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  private resetDomainRelatedServicesSettings() {
    this.form.get('serviceInfo.payrollPackage').setValidators([]);
    this.form.get('serviceInfo.payrollPayDay').setValidators([]);

    this.form.get('serviceInfo.enablePayrollPackage').setValue(false);
    this.form.get('serviceInfo.payrollPackage').setValue(null);
    this.form.get('serviceInfo.payrollPayDay').setValue(null)
    this.form.get('serviceInfo.executePayrollPayments').setValue(false);
    this.form.get('serviceInfo.executePayrollPaymentsBeforeBankHolidays').setValue(false);
  }
}

